/*
 * Figma `125:194` — SA3-04 · Delivery Operations.
 *
 * `SafetyTemplate`: what is actually on right now, then what turning each switch does to people
 * mid-purchase, then the switches, then the per-event overrides.
 *
 * Health first, and health means the *effective* state — the server's own answer, computed from a
 * deployment flag and a runtime switch together. The defect this panel was built for was an
 * operator turning on the deployment flag and seeing no change, because the runtime layer above it
 * was still off with nothing on screen saying so. So `lineBlockedBy` is rendered as the headline
 * when it is set, rather than as a footnote under a switch that looks enabled.
 *
 * The consequences are stated beside the switch rather than in the dialog after it. A kill switch
 * whose cost is only revealed once you press it is a kill switch that gets pressed to find out.
 */
import {
  Alert, Button, Card, ReadinessCard, ReadinessRow, SafetyTemplate, Select, StatusBadge, Toggle,
} from '../..';
import type { Destination } from '../../destinations';
import type { LineRolloutMode, TenantSummaryDto } from '../../../api';
import type { DeliveryRolloutState } from './useDeliveryRollout';
import { useDeliveryRollout } from './useDeliveryRollout';

const MODE_LABEL: Record<LineRolloutMode, string> = {
  off: 'ปิดทั้งแพลตฟอร์ม',
  canary: 'เปิดเฉพาะอีเวนต์ที่เลือก',
  all: 'เปิดทุกอีเวนต์',
};

export function DeliveryOperationsScreen({ destination, state, events }: {
  destination: Destination;
  state: DeliveryRolloutState;
  events: readonly TenantSummaryDto[];
}) {
  const {
    status, loading, loadError, error, busy, justSaved,
    setMode, setMaster, setSelectionV2, setPortal, setCanaryEvents, setEventOverride, reload,
  } = state;

  const effective = status?.effective;
  const runtime = status?.runtime;
  const deployment = status?.deployment;
  const canary = runtime?.lineCanaryEventIds ?? [];
  const overrides = runtime?.organizerLineOverrides ?? {};

  return (
    <SafetyTemplate
      gate={{ destination, loading, error: loadError || null, onRetry: reload }}
      health={
        <>
          <ReadinessRow label="สถานะการส่งมอบที่มีผลจริง">
            <ReadinessCard
              title="ส่งบัตรทาง LINE"
              status={effective?.lineDelivery ? 'เปิดอยู่' : 'ปิดอยู่'}
              tone={effective?.lineDelivery ? 'ready' : 'attention'}
              detail={effective?.lineBlockedBy
                ? `ถูกปิดโดย ${effective.lineBlockedBy}`
                : `โหมด ${MODE_LABEL[runtime?.lineRolloutMode ?? 'off']}`}
            />
            <ReadinessCard
              title="ให้ผู้ซื้อเลือกช่องทาง"
              status={effective?.deliverySelectionV2 ? 'เปิดอยู่' : 'ปิดอยู่'}
              tone={effective?.deliverySelectionV2 ? 'ready' : 'unknown'}
              detail={deployment?.deliverySelectionV2 ? 'deployment flag เปิดอยู่' : 'deployment flag ปิดอยู่'}
            />
            <ReadinessCard
              title="หน้า /myticket"
              status={effective?.myTicketPortal ? 'เปิดอยู่' : 'ปิดอยู่'}
              tone={effective?.myTicketPortal ? 'ready' : 'attention'}
              detail={effective?.myTicketPortal
                ? 'ผู้ซื้อกลับมาดูบัตรเองได้'
                : 'ผู้ซื้อที่ปิดหน้าจอไปแล้วจะไม่มีทางกลับมาหาบัตร'}
            />
          </ReadinessRow>

          {effective?.lineBlockedBy && (
            <Alert tone="warning" title="สวิตช์ runtime เปิดอยู่ แต่ยังส่งไม่ได้">
              {effective.lineBlockedBy} ปิดอยู่ที่ระดับ deployment — เปลี่ยนที่นี่อย่างเดียวไม่พอ
              ต้องแก้ env ตอน deploy ด้วย มิฉะนั้นหน้าจอนี้จะบอกว่าเปิด แต่ไม่มีบัตรถูกส่งออกไปเลย
            </Alert>
          )}
          {error && <Alert tone="danger" title="บันทึกไม่สำเร็จ">{error}</Alert>}
        </>
      }
      consequence={
        <Card title="สิ่งที่จะเกิดกับผู้ซื้อ" sub="อ่านก่อนสับสวิตช์ใด ๆ ระหว่างรอบขาย">
          <ul className="jt-consequences">
            <li>ปิด LINE ระหว่างรอบขาย ออร์เดอร์ใหม่จะไม่ส่งทาง LINE — ออร์เดอร์ที่ส่งไปแล้วไม่ถูกเรียกคืน</li>
            <li>ปิด “ให้ผู้ซื้อเลือกช่องทาง” จะเปลี่ยนรูปแบบ checkout ของทุกอีเวนต์ทันที ไม่ใช่แค่ของใหม่</li>
            <li>ปิด /myticket ผู้ซื้อที่ไม่ได้เก็บลิงก์ไว้จะกลับมาหาบัตรของตัวเองไม่ได้เลย</li>
          </ul>
        </Card>
      }
      configuration={
        <>
          <Card title="สวิตช์ระดับแพลตฟอร์ม">
            <Toggle
              label="เปิดการส่งบัตรทาง LINE"
              checked={!!runtime?.lineDeliveryEnabled}
              disabled={busy}
              onChange={(e) => void setMaster(e.target.checked)}
              consequence="สวิตช์หลัก — ปิดแล้วโหมดด้านล่างไม่มีผล"
              trailing={deployment?.lineDelivery
                ? <StatusBadge tone="success">deployment เปิด</StatusBadge>
                : <StatusBadge tone="danger">deployment ปิด</StatusBadge>}
            />
            <Select
              label="ขอบเขตการเปิด LINE"
              value={runtime?.lineRolloutMode ?? 'off'}
              disabled={busy}
              options={(['off', 'canary', 'all'] as LineRolloutMode[]).map((m) => ({ value: m, label: MODE_LABEL[m] }))}
              onChange={(e) => void setMode(e.target.value as LineRolloutMode)}
              hint="ทยอยเปิดทีละอีเวนต์ก่อนเปิดทั้งแพลตฟอร์ม"
            />
            <Toggle
              label="ให้ผู้ซื้อเลือกช่องทางรับบัตร"
              checked={!!runtime?.deliverySelectionV2Enabled}
              disabled={busy}
              onChange={(e) => void setSelectionV2(e.target.checked)}
              consequence="เปลี่ยนขั้นตอน checkout ของทุกอีเวนต์พร้อมกัน"
            />
            <Toggle
              label="เปิดหน้า /myticket"
              checked={!!runtime?.myTicketPortalEnabled}
              disabled={busy}
              onChange={(e) => void setPortal(e.target.checked)}
              consequence="ทางเดียวที่ผู้ซื้อกลับมาหาบัตรเองได้ถ้าไม่ได้เก็บลิงก์ไว้"
            />
          </Card>

          {runtime?.lineRolloutMode === 'canary' && (
            <Card title="อีเวนต์ที่เปิด LINE" sub="เฉพาะอีเวนต์ที่เลือกไว้เท่านั้นที่จะส่งทาง LINE">
              {events.length === 0 && <p className="jt-field-hint">ยังไม่มีอีเวนต์ให้เลือก</p>}
              {events.map((event) => (
                <Toggle
                  key={event.id}
                  label={event.name || event.slug}
                  checked={canary.includes(event.id)}
                  disabled={busy}
                  onChange={(e) => void setCanaryEvents(
                    e.target.checked ? [...canary, event.id] : canary.filter((id) => id !== event.id),
                  )}
                  consequence={event.status === 'active' ? 'อีเวนต์นี้กำลังขายอยู่' : `สถานะ ${event.status}`}
                />
              ))}
            </Card>
          )}

          <Card
            title="ตั้งค่าเฉพาะอีเวนต์"
            sub="ทับค่าของแพลตฟอร์มสำหรับอีเวนต์เดียว — ใช้เมื่อผู้จัดรายนั้นมีปัญหาเฉพาะตัว"
          >
            {events.map((event) => {
              const override = overrides[event.id];
              const value = override === undefined ? 'inherit' : override ? 'on' : 'off';
              return (
                <Select
                  key={event.id}
                  label={event.name || event.slug}
                  value={value}
                  disabled={busy}
                  options={[
                    { value: 'inherit', label: 'ตามค่าแพลตฟอร์ม' },
                    { value: 'on', label: 'บังคับเปิด LINE' },
                    { value: 'off', label: 'บังคับปิด LINE' },
                  ]}
                  onChange={(e) => void setEventOverride(
                    event.id,
                    e.target.value === 'inherit' ? null : e.target.value === 'on',
                  )}
                />
              );
            })}
          </Card>
        </>
      }
      /*
       * No save bar: every switch on this page applies the moment it is thrown. That is worth
       * saying out loud rather than leaving people to discover it — an operator looking for the
       * save button during an incident is an operator who has already changed production.
       */
      dangerZone={
        <Card title="การเปลี่ยนแปลงมีผลทันที">
          <p className="jt-field-hint">
            ทุกสวิตช์บนหน้านี้ส่งถึงเซิร์ฟเวอร์ทันทีที่กด ไม่มีขั้นตอนบันทึกแยก
            {justSaved && ' · บันทึกครั้งล่าสุดสำเร็จแล้ว'}
          </p>
          <div className="jt-inline-actions">
            <Button size="sm" variant="secondary" disabled={busy} onClick={() => void reload()}>
              {busy ? 'กำลังทำงาน…' : 'โหลดสถานะจากเซิร์ฟเวอร์ใหม่'}
            </Button>
          </div>
        </Card>
      }
    />
  );
}

export function DeliveryOperationsDestination({ token, destination, events }: {
  token: string;
  destination: Destination;
  events: readonly TenantSummaryDto[];
}) {
  const state = useDeliveryRollout(token);
  return <DeliveryOperationsScreen destination={destination} state={state} events={events} />;
}
